import React from "react";
import { Grid, IconButton, Typography, Button } from "@mui/material";
import { AddCircleOutline, Delete } from "@mui/icons-material";
import Controls from "../../atoms/Controls";
import CopaymentExemptionUpload from "./CopaymentExemptionUpload";

export interface CopaymentExemptionFormValue {
  id?: string;
  startDate?: string | null;
  endDate?: string | null;
  documentId?: string | null;
  documentName?: string | null;
}

interface CopaymentExemptionFormProps {
  patientId: number;
  exemptions: CopaymentExemptionFormValue[];
  errors?: string;
  onAdd: () => void;
  onRemove: (id: string) => void;
  onChange: (
    id: string,
    field: "startDate" | "endDate",
    value: string | null,
  ) => void;
  onSetYear: (exemptionId: string, year: number) => void;
  onDocumentUploadSuccess: (
    exemptionId: string,
    documentId: string,
    documentName: string,
  ) => void;
  disabled?: boolean;
}

const CopaymentExemptionForm: React.FC<CopaymentExemptionFormProps> = ({
  patientId,
  exemptions,
  errors,
  onAdd,
  onRemove,
  onChange,
  onSetYear,
  onDocumentUploadSuccess,
  disabled = false,
}) => {
  const currentYear = new Date().getFullYear();

  const usedDocumentIds = exemptions
    .map((e) => e.documentId)
    .filter((id): id is string => !!id);

  return (
    <div>
      <Typography
        variant="subtitle2"
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.5,
          marginBottom: 1,
        }}
      >
        Zuzahlungsbefreiung:{" "}
        {exemptions.length > 0 ? (
          <Typography
            component="span"
            variant="subtitle2"
            color="success.main"
            fontWeight="bold"
          >
            befreit
          </Typography>
        ) : (
          <Typography
            component="span"
            variant="subtitle2"
            color="text.secondary"
            fontWeight="bold"
          >
            pflichtig
          </Typography>
        )}
        <IconButton size="small" onClick={onAdd} disabled={disabled}>
          <AddCircleOutline fontSize="small" />
          <Typography variant="caption" sx={{ marginLeft: 0.5 }}>
            Befreiungszeiträume hinzufügen
          </Typography>
        </IconButton>
      </Typography>
      {errors && (
        <Typography variant="caption" color="error">
          {errors}
        </Typography>
      )}
      {exemptions.length === 0 ? (
        <Typography variant="body2" color="text.secondary" />
      ) : (
        exemptions.map(
          (exemption: CopaymentExemptionFormValue, index: number) => {
            const exemptionId = exemption.id || `${index}`;
            return (
              <Grid
                container
                spacing={1}
                alignItems="center"
                key={exemptionId}
                sx={{ mt: 1 }}
              >
                <Grid
                  item
                  xs={12}
                  md={0.8}
                  sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}
                >
                  <Button
                    size="small"
                    onClick={() => onSetYear(exemptionId, currentYear - 1)}
                    sx={{
                      fontSize: "0.7rem",
                      padding: "2px 4px",
                      minWidth: "auto",
                    }}
                    disabled={disabled}
                  >
                    {currentYear - 1}
                  </Button>
                  <Button
                    size="small"
                    onClick={() => onSetYear(exemptionId, currentYear)}
                    sx={{
                      fontSize: "0.7rem",
                      padding: "2px 4px",
                      minWidth: "auto",
                    }}
                    disabled={disabled}
                  >
                    {currentYear}
                  </Button>
                  <Button
                    size="small"
                    onClick={() => onSetYear(exemptionId, currentYear + 1)}
                    sx={{
                      fontSize: "0.7rem",
                      padding: "2px 4px",
                      minWidth: "auto",
                    }}
                    disabled={disabled}
                  >
                    {currentYear + 1}
                  </Button>
                </Grid>
                <Grid item xs={12} md={2.3}>
                  <Controls.DatePicker
                    name={`copaymentExemptions[${index}].startDate`}
                    label="Startdatum"
                    value={exemption.startDate}
                    onChange={(event: any) =>
                      onChange(exemptionId, "startDate", event.target.value)
                    }
                    disabled={disabled}
                  />
                </Grid>
                <Grid item xs={12} md={2.3}>
                  <Controls.DatePicker
                    name={`copaymentExemptions[${index}].endDate`}
                    label="Enddatum"
                    value={exemption.endDate}
                    onChange={(event: any) =>
                      onChange(exemptionId, "endDate", event.target.value)
                    }
                    minDate={exemption.startDate || undefined}
                    disabled={disabled}
                  />
                </Grid>
                <Grid
                  item
                  xs={12}
                  md={4.4}
                  sx={{ display: "flex", alignItems: "center", gap: 1 }}
                >
                  <CopaymentExemptionUpload
                    patientId={patientId}
                    exemptionId={exemptionId}
                    documentId={exemption.documentId}
                    onUploadSuccess={(documentId, documentName) =>
                      onDocumentUploadSuccess(
                        exemptionId,
                        documentId,
                        documentName,
                      )
                    }
                    excludeDocumentIds={usedDocumentIds}
                    disabled={disabled}
                  />
                  {exemption.documentName && (
                    <Typography
                      variant="caption"
                      sx={{
                        flexGrow: 1,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                      title={exemption.documentName}
                    >
                      {exemption.documentName.split("/").pop() ||
                        exemption.documentName}
                    </Typography>
                  )}
                </Grid>
                <Grid item xs={12} md={2}>
                  <IconButton
                    aria-label="Zeitraum entfernen"
                    onClick={() => onRemove(exemptionId)}
                    disabled={disabled}
                  >
                    <Delete fontSize="small" />
                  </IconButton>
                </Grid>
              </Grid>
            );
          },
        )
      )}
    </div>
  );
};

export default CopaymentExemptionForm;
